import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

const TIMELINE_OPTIONS = [
  "7 Days",
  "30 Days",
  "60 Days",
  "90 Days",
  "180 Days",
  "1 Year",
  "2 Years",
  "5 Years",
  "10 Years",
];

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function formatPercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${num.toFixed(2)}%`;
}

function computeReturnPct(startPrice, endPrice) {
  const start = Number(startPrice);
  const end = Number(endPrice);

  if (!Number.isFinite(start) || !Number.isFinite(end) || start === 0) {
    return null;
  }

  return ((end - start) / start) * 100;
}

function getRealReturn(confidence, returnPct) {
  const c = Number(confidence);
  const r = Number(returnPct);

  if (!Number.isFinite(c) || !Number.isFinite(r)) return null;
  return c * r;
}

const pageStyle = {
  maxWidth: 1400,
  margin: "0 auto",
  padding: 24,
};

const cardGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
  marginBottom: 28,
};

const cardStyle = {
  border: "1px solid #ddd",
  borderRadius: 14,
  padding: 18,
  background: "#fff",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const labelStyle = {
  display: "block",
  fontWeight: 600,
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: 12,
  border: "1px solid #ccc",
  borderRadius: 8,
  fontSize: 14,
  boxSizing: "border-box",
};

const thStyle = {
  textAlign: "left",
  padding: "12px 10px",
  borderBottom: "1px solid #e5e5e5",
  background: "#fafafa",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  verticalAlign: "top",
};

const tdStyle = {
  padding: "12px 10px",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "top",
  fontSize: 14,
};

function StatCard({ title, value, subtext }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>{value}</div>
      {subtext ? (
        <div style={{ marginTop: 8, fontSize: 12, color: "#777" }}>{subtext}</div>
      ) : null}
    </div>
  );
}

export default function Convictions() {
  const { user } = useAuth();

  const [convictions, setConvictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [ticker, setTicker] = useState("");
  const [confidence, setConfidence] = useState(3);
  const [timeline, setTimeline] = useState("90 Days");
  const [rationale, setRationale] = useState("");

  async function loadConvictions() {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    const { data, error: loadError } = await supabase
      .from("convictions")
      .select("*")
      .order("created_at", { ascending: false });

    if (loadError) {
      setError(loadError.message);
      setLoading(false);
      return;
    }

    setConvictions(data || []);
    setLoading(false);
  }

  useEffect(() => {
    loadConvictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const convictionsWithComputed = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (convictions || []).map((row) => {
      const startDateObj = row.start_date ? new Date(row.start_date) : null;
      const endDateObj = row.end_date ? new Date(row.end_date) : null;

      const hasStarted =
        startDateObj instanceof Date &&
        !Number.isNaN(startDateObj?.getTime?.()) &&
        startDateObj <= today;

      const closedByFlag = Boolean(row.is_closed);
      const autoClosed =
        endDateObj instanceof Date &&
        !Number.isNaN(endDateObj?.getTime?.()) &&
        endDateObj < today;

      const closed = closedByFlag || autoClosed;

      const effectiveEndPrice = closed
        ? (row.close_price ?? row.latest_price)
        : row.latest_price;

      const returnPct = computeReturnPct(row.start_price, effectiveEndPrice);
      const realReturn = getRealReturn(row.confidence, returnPct);

      return {
        ...row,
        hasStarted,
        closed,
        effectiveEndPrice,
        returnPct,
        realReturn,
      };
    });
  }, [convictions]);

  const currentConvictions = useMemo(
    () => convictionsWithComputed.filter((c) => !c.closed),
    [convictionsWithComputed]
  );

  const completedConvictions = useMemo(
    () => convictionsWithComputed.filter((c) => c.closed),
    [convictionsWithComputed]
  );

  const openConvictionCount = currentConvictions.length;

  const totalClosedRealReturn = useMemo(() => {
    return completedConvictions.reduce((sum, row) => {
      return sum + (Number.isFinite(row.realReturn) ? row.realReturn : 0);
    }, 0);
  }, [completedConvictions]);

  const currentRealReturnIncludingOpen = useMemo(() => {
    return convictionsWithComputed.reduce((sum, row) => {
      return sum + (Number.isFinite(row.realReturn) ? row.realReturn : 0);
    }, 0);
  }, [convictionsWithComputed]);

async function handleCreateConviction(e) {
  e.preventDefault();
  setError("");
  setSuccess("");

  if (!user) {
    setError("You must be logged in.");
    return;
  }

  if (openConvictionCount >= 20) {
    setError("You already have the maximum of 20 current convictions.");
    return;
  }

  const cleanTicker = ticker.trim().toUpperCase();
  const cleanRationale = rationale.trim();

  if (!cleanTicker) {
    setError("Ticker is required.");
    return;
  }

  if (!cleanRationale) {
    setError("Rationale is required.");
    return;
  }

  setSaving(true);

  const {
  data: { session },
  error: sessionError,
} = await supabase.auth.getSession();

console.log("session error:", sessionError);
console.log("session:", session);
console.log("access token:", session?.access_token);

  try {
    const { data, error: functionError } = await supabase.functions.invoke(
      "create-conviction",
      {
        body: {
          ticker: cleanTicker,
          confidence: Number(confidence),
          timeline,
          rationale: cleanRationale,
        },
      }
    );

    console.log("create-conviction data:", data);
    console.log("create-conviction error:", functionError);

    if (functionError) {
      let detailedMessage = functionError.message || "Failed to create conviction.";

      try {
        if (functionError.context) {
          const raw = await functionError.context.text();
          console.log("create-conviction raw error body:", raw);

          try {
            const parsed = JSON.parse(raw);
            detailedMessage = parsed?.error || raw || detailedMessage;
          } catch {
            detailedMessage = raw || detailedMessage;
          }
        }
      } catch (readErr) {
        console.error("Could not read function error body:", readErr);
      }

      setError(detailedMessage);
      setSaving(false);
      return;
    }

    setTicker("");
    setConfidence(3);
    setTimeline("90 Days");
    setRationale("");
    setSuccess("Conviction created. It is now permanently recorded on your page.");
    setSaving(false);

    await loadConvictions();
  } catch (err) {
    console.error("handleCreateConviction unexpected error:", err);
    setError(err?.message || "Failed to create conviction.");
    setSaving(false);
  }
}

  return (
    <div style={pageStyle}>
      <h1 style={{ marginBottom: 8 }}>Convictions</h1>
      <p style={{ color: "#666", marginTop: 0, marginBottom: 24 }}>
        Create permanent stock convictions that stay on your record until timeline expiry.
        New convictions start at the next available trading day open.
      </p>

      <div style={cardGridStyle}>
        <StatCard
          title="Total Real Return (Closed Convictions)"
          value={formatPercent(totalClosedRealReturn)}
        />
        <StatCard
          title="Current Real Return (Including Open Convictions)"
          value={formatPercent(currentRealReturnIncludingOpen)}
        />
        <StatCard
          title="Current Convictions"
          value={`${openConvictionCount} / 20`}
          subtext="Maximum 20 open convictions at one time"
        />
        <StatCard
          title="Completed Convictions"
          value={`${completedConvictions.length}`}
        />
      </div>

      <div style={{ ...cardStyle, marginBottom: 28 }}>
        <h2 style={{ marginTop: 0 }}>Create Conviction</h2>
        <p style={{ color: "#666", marginTop: 0 }}>
          Once created, a conviction cannot be edited, deleted, or retracted.
        </p>

        <form onSubmit={handleCreateConviction}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <label style={labelStyle}>Stock (Ticker)</label>
              <input
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                placeholder="AAPL"
                maxLength={10}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Conviction Confidence</label>
              <select
                value={confidence}
                onChange={(e) => setConfidence(Number(e.target.value))}
                style={inputStyle}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Timeline</label>
              <select
                value={timeline}
                onChange={(e) => setTimeline(e.target.value)}
                style={inputStyle}
              >
                {TIMELINE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Rationale</label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Explain the thesis behind this conviction..."
              rows={5}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>

          {error ? (
            <div style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</div>
          ) : null}

          {success ? (
            <div style={{ color: "#15803d", marginBottom: 12 }}>{success}</div>
          ) : null}

          <button
            type="submit"
            disabled={saving || openConvictionCount >= 20}
            style={{
              padding: "12px 18px",
              borderRadius: 8,
              border: "1px solid #ccc",
              background: openConvictionCount >= 20 ? "#f3f3f3" : "#fff",
              cursor:
                saving || openConvictionCount >= 20 ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            {saving ? "Creating..." : "Create Conviction"}
          </button>
        </form>
      </div>

      <div style={{ ...cardStyle, marginBottom: 28 }}>
        <h2 style={{ marginTop: 0 }}>Current Convictions</h2>

        {loading ? (
          <p>Loading current convictions...</p>
        ) : currentConvictions.length === 0 ? (
          <p>No current convictions.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Ticker</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Start Date</th>
                  <th style={thStyle}>End Date</th>
                  <th style={thStyle}>Confidence</th>
                  <th style={thStyle}>Timeline</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Start Price</th>
                  <th style={thStyle}>Latest Price</th>
                  <th style={thStyle}>Current Return</th>
                  <th style={thStyle}>Real Return</th>
                  <th style={thStyle}>Rationale</th>
                </tr>
              </thead>
              <tbody>
                {currentConvictions.map((row) => {
                  const pending = !row.hasStarted || !row.start_price;

                  return (
                    <tr key={row.id}>
                      <td style={tdStyle}>
                        <strong>{row.ticker}</strong>
                      </td>
                      <td style={tdStyle}>{formatDateTime(row.created_at)}</td>
                      <td style={tdStyle}>{formatDate(row.start_date)}</td>
                      <td style={tdStyle}>{formatDate(row.end_date)}</td>
                      <td style={tdStyle}>{row.confidence}</td>
                      <td style={tdStyle}>{row.timeline}</td>
                      <td style={tdStyle}>
                        {pending ? (
                          <span style={{ color: "#a16207", fontWeight: 600 }}>Pending</span>
                        ) : (
                          <span style={{ color: "#15803d", fontWeight: 600 }}>Active</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {row.start_price != null ? row.start_price : "Pending"}
                      </td>
                      <td style={tdStyle}>
                        {row.latest_price != null ? row.latest_price : "—"}
                      </td>
                      <td style={tdStyle}>{formatPercent(row.returnPct)}</td>
                      <td style={tdStyle}>{formatPercent(row.realReturn)}</td>
                      <td style={{ ...tdStyle, maxWidth: 380, whiteSpace: "pre-wrap" }}>
                        {row.rationale}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Completed Convictions</h2>

        {loading ? (
          <p>Loading completed convictions...</p>
        ) : completedConvictions.length === 0 ? (
          <p>No completed convictions.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Ticker</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Start Date</th>
                  <th style={thStyle}>End Date</th>
                  <th style={thStyle}>Confidence</th>
                  <th style={thStyle}>Timeline</th>
                  <th style={thStyle}>Start Price</th>
                  <th style={thStyle}>Close Price</th>
                  <th style={thStyle}>Total Return</th>
                  <th style={thStyle}>Real Return</th>
                  <th style={thStyle}>Rationale</th>
                </tr>
              </thead>
              <tbody>
                {completedConvictions.map((row) => (
                  <tr key={row.id}>
                    <td style={tdStyle}>
                      <strong>{row.ticker}</strong>
                    </td>
                    <td style={tdStyle}>{formatDateTime(row.created_at)}</td>
                    <td style={tdStyle}>{formatDate(row.start_date)}</td>
                    <td style={tdStyle}>{formatDate(row.end_date)}</td>
                    <td style={tdStyle}>{row.confidence}</td>
                    <td style={tdStyle}>{row.timeline}</td>
                    <td style={tdStyle}>
                      {row.start_price != null ? row.start_price : "—"}
                    </td>
                    <td style={tdStyle}>
                      {row.close_price != null ? row.close_price : "—"}
                    </td>
                    <td style={tdStyle}>{formatPercent(row.returnPct)}</td>
                    <td style={tdStyle}>{formatPercent(row.realReturn)}</td>
                    <td style={{ ...tdStyle, maxWidth: 380, whiteSpace: "pre-wrap" }}>
                      {row.rationale}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

